import { getStreamUrlDatasetStream } from "@/helpers/datasetAttributeHelper";
import {
  Dataset,
  DatasetChunkStream,
  DatasetData,
  DatasetFilter,
  DatasetMetadata,
  DatasetMetadataStream,
  H3Configuration
} from "@/types/dataset";

interface IFetchDatasetStreamProps {
  datasetId?: string;
  filters?: DatasetFilter[];
  timezone?: { code: string; name: string };
  h3Configuration?: H3Configuration | null;
  onOpen?: () => void;
  onDatasetMetadata?: (data: DatasetMetadata) => void;
  onError?: (data: unknown) => void;
  generateDatasetData: (
    data: DatasetChunkStream,
    metadata: DatasetMetadataStream,
    isSpatialAnalysisResult: boolean,
    h3Configuration?: H3Configuration | null
  ) => {
    datasetData: DatasetData;
    newDataset: Dataset;
  };
}

export function fetchDatasetStream({
  datasetId,
  filters,
  h3Configuration,
  timezone,
  generateDatasetData,
  onOpen,
  onError,
  onDatasetMetadata
}: IFetchDatasetStreamProps): Promise<{
  datasetMetadata: DatasetMetadata | undefined;
  rawDataset: DatasetData | undefined;
  dataset: Dataset | undefined;
}> {
  return new Promise((resolve, reject) => {
    let chunkBuffer: DatasetChunkStream["rows"] = [];
    let datasetMetadata: DatasetMetadata | undefined = undefined;
    let datasetMetadataStream: DatasetMetadataStream | undefined = undefined;

    let datasetData:
      | {
          rawDataset: DatasetData;
          dataset: Dataset;
        }
      | undefined = undefined;

    const url = getStreamUrlDatasetStream({
      filters,
      timezone,
      h3Configuration,
      datasetId
    });
    const es = new EventSource(url, { withCredentials: true });

    es.onopen = () => {
      onOpen?.();
    };

    const close = () => {
      es.close();
    };

    es.addEventListener("dataset_detail", (evt) => {
      datasetMetadata = JSON.parse(evt.data);
      if (datasetMetadata) {
        onDatasetMetadata?.(datasetMetadata);
      }
    });

    es.addEventListener("dataset_data:meta", (evt) => {
      datasetMetadataStream = JSON.parse(evt.data);
    });

    es.addEventListener("dataset_data:chunk", (evt) => {
      const payload = JSON.parse(evt.data);
      chunkBuffer.push(...payload.rows);
    });

    es.addEventListener("dataset_data:done", (evt) => {
      const payload = JSON.parse(evt.data);

      if (datasetMetadata && datasetMetadataStream) {
        const result = generateDatasetData(
          {
            dataset_id: payload.dataset_id,
            rows: chunkBuffer,
            seq: payload.final_seq
          },
          datasetMetadataStream,
          true,
          h3Configuration
        );

        datasetData = {
          rawDataset: result.datasetData,
          dataset: result.newDataset
        };
      }

      chunkBuffer = [];
    });

    es.addEventListener("stream:done", () => {
      if (datasetMetadata && datasetData && datasetMetadataStream) {
        close();
        resolve({
          datasetMetadata,
          ...datasetData
        });
      }
    });

    es.addEventListener("stream:error", (evt) => {
      close();
      const err = JSON.parse(evt.data);
      onError?.(err);
      reject(err);
    });

    es.onerror = (evt) => {
      close();
      onError?.(evt);
      reject(new Error("An error occurred while fetching the dataset stream."));
    };
  });
}
